class VantaCheckout {
  constructor(root) {
    this.root = root;
    this.form = root.querySelector('[data-vanta-checkout-form]');
    this.error = root.querySelector('[data-vanta-checkout-error]');
    root.querySelector('[data-vanta-checkout-back]')?.addEventListener('click', (event) => {
      if (history.length <= 1) return;
      event.preventDefault();
      history.back();
    });
    this.initialize();
  }

  async initialize() {
    try {
      this.recovery = this.loadRecovery();
      this.draft = await this.loadDraft();
      const data = await this.request('/apps/vanta/commerce/quote', this.draft);
      this.pricing = data.pricing;
      this.settings = data.settings;
      this.locations = data.locations || {};
      this.shippingRates = [];
      this.selectedShippingRate = null;
      this.renderSummary();
      this.configureForm();
      this.configureValidation();
      this.root.querySelector('[data-vanta-checkout-content]').hidden = false;
      this.form.addEventListener('submit', (event) => this.submit(event));
    } catch (error) {
      this.showError(error.message || 'No pudimos cargar el pedido.');
    }
  }

  async loadDraft() {
    const source = new URLSearchParams(location.search).get('source');
    if (this.recovery?.draft?.source === source) return this.recovery.draft;
    if (source === 'buy_now') {
      const saved = sessionStorage.getItem('vanta_purchase_draft');
      if (!saved) throw new Error('Tu selección expiró. Vuelve al producto e inténtalo de nuevo.');
      return JSON.parse(saved);
    }
    const cart = await fetch('/cart.js').then((response) => response.json());
    if (!cart.items?.length) throw new Error('Tu carrito está vacío.');
    const groups = new Map();
    cart.items.forEach((item) => {
      const offerGroup = item.properties?._vanta_offer_group;
      const id = offerGroup || `normal-${item.key}`;
      const group = groups.get(id) || {
        id,
        productId: `gid://shopify/Product/${item.product_id}`,
        offerId: item.properties?._vanta_offer_id || undefined,
        offerTierId: item.properties?._vanta_offer_tier || undefined,
        lines: [],
      };
      group.lines.push({
        variantId: `gid://shopify/ProductVariant/${item.variant_id}`,
        quantity: item.quantity,
        offerId: group.offerId,
        offerTierId: group.offerTierId,
        offerGroupId: id,
      });
      groups.set(id, group);
    });
    return { source: 'cart', groups: [...groups.values()] };
  }

  loadRecovery() {
    const params = new URLSearchParams(location.search);
    const token = params.get('resume');
    if (!token) return null;
    const key = `vanta_checkout_recovery_${token}`;
    try {
      const recovery = JSON.parse(sessionStorage.getItem(key) || 'null');
      const isCurrent = recovery?.draft?.source === params.get('source');
      const isFresh = Date.now() - Number(recovery?.savedAt || 0) < 24 * 60 * 60 * 1000;
      if (isCurrent && isFresh && recovery.draft.groups?.length) return recovery;
    } catch {
      // Invalid recovery data is discarded below.
    }
    sessionStorage.removeItem(key);
    return null;
  }

  saveRecovery(draft) {
    const url = new URL(location.href);
    const token = url.searchParams.get('resume') || crypto.randomUUID();
    const recovery = { savedAt: Date.now(), draft };
    sessionStorage.setItem(`vanta_checkout_recovery_${token}`, JSON.stringify(recovery));
    url.searchParams.set('resume', token);
    history.replaceState(history.state, '', `${url.pathname}${url.search}${url.hash}`);
    this.recovery = recovery;
  }

  clearRecovery() {
    const token = new URLSearchParams(location.search).get('resume');
    if (token) sessionStorage.removeItem(`vanta_checkout_recovery_${token}`);
  }

  async request(url, body) {
    const response = await fetch(url, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) });
    const data = await response.json();
    if (!response.ok) throw new Error(data.error || 'No pudimos procesar la solicitud.');
    return data;
  }

  formatMoney(value) {
    return new Intl.NumberFormat('es-CO', { style: 'currency', currency: 'COP', maximumFractionDigits: 0 }).format(value);
  }

  renderSummary() {
    const lines = this.pricing.groups.flatMap((group) => group.lines);
    const linesMarkup = lines.map((line) => `<div class="vanta-checkout__line">${line.imageUrl ? `<img src="${line.imageUrl}" alt="">` : '<span></span>'}<span><strong>${line.productTitle}</strong><small>${line.title} · Cantidad ${line.quantity}</small></span><strong>${this.formatMoney(line.lineTotal)}</strong></div>`).join('');
    const mobileLinesMarkup = lines.map((line) => `<div class="vanta-checkout__line"><span class="vanta-checkout__line-media">${line.imageUrl ? `<img src="${line.imageUrl}" alt="">` : ''}<b>${line.quantity}</b></span><span><strong>${line.productTitle}</strong><small>${line.title}</small></span><strong>${this.formatMoney(line.lineTotal)}</strong></div>`).join('');
    this.root.querySelector('[data-vanta-order-lines]').innerHTML = linesMarkup;
    const itemCount = lines.reduce((sum, line) => sum + line.quantity, 0);
    const summaryImages = lines.flatMap((line) => Array.from({ length: line.quantity }, () => line.imageUrl)).slice(0, 3);
    this.root.querySelectorAll('[data-vanta-mobile-summary]').forEach((summary) => {
      summary.querySelector('[data-vanta-mobile-order-lines]').innerHTML = mobileLinesMarkup;
      summary.querySelector('[data-vanta-mobile-item-count]').textContent = `${itemCount} ${itemCount === 1 ? 'artículo' : 'artículos'}`;
      summary.querySelector('[data-vanta-mobile-subtotal-label]').textContent = `Subtotal · ${itemCount} ${itemCount === 1 ? 'artículo' : 'artículos'}`;
      const summaryImage = summary.querySelector('[data-vanta-mobile-summary-image]');
      if (summaryImage) {
        summaryImage.style.width = `${5.2 + Math.max(0, summaryImages.length - 1) * 0.9}rem`;
        summaryImage.innerHTML = summaryImages.map((imageUrl, index) => `<span class="vanta-checkout__mobile-summary-thumb" style="--thumb-index:${index};--thumb-layer:${summaryImages.length - index}">${imageUrl ? `<img src="${imageUrl}" alt="">` : ''}</span>`).join('');
      }
    });
    this.root.querySelector('[data-subtotal]').textContent = this.formatMoney(this.pricing.subtotal);
    this.root.querySelector('[data-discount]').textContent = this.pricing.discount ? `-${this.formatMoney(this.pricing.discount)}` : this.formatMoney(0);
    this.root.querySelectorAll('[data-vanta-mobile-subtotal]').forEach((element) => { element.textContent = this.formatMoney(this.pricing.subtotal); });
    this.root.querySelectorAll('[data-vanta-mobile-discount]').forEach((element) => { element.textContent = `-${this.formatMoney(this.pricing.discount)}`; });
    this.root.querySelectorAll('[data-vanta-mobile-discount-row]').forEach((element) => { element.hidden = !this.pricing.discount; });
    this.renderTotals();
  }

  renderTotals() {
    const shipping = this.selectedShippingRate?.amount;
    this.root.querySelector('[data-shipping]').textContent = shipping === undefined
      ? 'Por calcular'
      : shipping > 0 ? this.formatMoney(shipping) : 'Gratis';
    const shippingText = shipping === undefined ? 'Por calcular' : shipping > 0 ? this.formatMoney(shipping) : 'Gratis';
    const total = this.formatMoney(this.pricing.subtotal - this.pricing.discount + (shipping || 0));
    this.root.querySelectorAll('[data-vanta-mobile-shipping]').forEach((element) => { element.textContent = shippingText; });
    this.root.querySelectorAll('[data-vanta-mobile-total]').forEach((element) => { element.textContent = total; });
    this.root.querySelector('[data-total]').textContent = total;
  }

  configureForm() {
    const codOption = this.root.querySelector('[data-cod-option]');
    codOption.hidden = !this.settings.codEnabled;
    codOption.querySelector('input').disabled = !this.settings.codEnabled;
    this.root.querySelector('[data-cod-label]').textContent = this.settings.paymentLabel;
    const email = this.form.elements.email;
    email.required = this.settings.emailRequired;
    this.root.querySelector('[data-email-required]').textContent = this.settings.emailRequired ? '*' : '';
    if (!this.settings.codEnabled) this.form.elements.paymentMethod.value = 'card_pse';
    this.form.querySelectorAll('[name="paymentMethod"]').forEach((field) => {
      field.addEventListener('change', () => this.updateSubmitLabel());
    });
    this.root.querySelectorAll('[data-vanta-mobile-summary-toggle]').forEach((summaryToggle) => {
      summaryToggle.addEventListener('click', () => {
        const details = summaryToggle.parentElement.querySelector('[data-vanta-mobile-summary-details]');
        const expanded = summaryToggle.getAttribute('aria-expanded') === 'true';
        summaryToggle.setAttribute('aria-expanded', String(!expanded));
        details.getAnimations().forEach((animation) => animation.cancel());

        if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
          details.hidden = expanded;
          return;
        }

        if (expanded) {
          const animation = details.animate(
            [
              { height: `${details.scrollHeight}px`, opacity: 1, transform: 'translateY(0)' },
              { height: '0px', opacity: 0, transform: 'translateY(-0.4rem)' },
            ],
            { duration: 180, easing: 'cubic-bezier(0.4, 0, 1, 1)' },
          );
          animation.onfinish = () => { details.hidden = true; };
          return;
        }

        details.hidden = false;
        details.animate(
          [
            { height: '0px', opacity: 0, transform: 'translateY(-0.4rem)' },
            { height: `${details.scrollHeight}px`, opacity: 1, transform: 'translateY(0)' },
          ],
          { duration: 240, easing: 'cubic-bezier(0.22, 1, 0.36, 1)' },
        );
      });
    });
    this.updateSubmitLabel();
    this.configureLocations();
    this.restoreRecovery();
    this.configureShipping();
  }

  updateSubmitLabel() {
    const label = this.form.elements.paymentMethod.value === 'cod'
      ? 'FINALIZAR EL PEDIDO'
      : 'PAGAR AHORA';
    this.form.querySelectorAll('[type="submit"]').forEach((button) => { button.textContent = label; });
  }

  async clearCart() {
    try {
      await fetch('/cart/clear.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: '{}',
      });
    } catch {
      // The confirmed order must continue even if the browser cart cannot be cleared.
    }
  }

  configureLocations() {
    const department = this.form.elements.department;
    Object.keys(this.locations).forEach((name) => department.add(new Option(name, name)));
    department.addEventListener('change', () => {
      this.populateCities(department.value);
      this.setFieldError(this.form.elements.city, '');
    });
  }

  populateCities(departmentName, selectedCity = '') {
    const city = this.form.elements.city;
    city.replaceChildren(new Option(
      departmentName ? 'Selecciona una ciudad' : 'Primero selecciona un departamento',
      '',
    ));
    (this.locations[departmentName] || []).forEach((name) => city.add(new Option(name, name)));
    city.disabled = !departmentName;
    city.value = selectedCity;
  }

  restoreRecovery() {
    const draft = this.recovery?.draft;
    if (!draft?.customer) return;
    const customer = draft.customer;
    ['fullName', 'phone', 'email', 'address', 'addressExtra'].forEach((name) => {
      if (this.form.elements[name]) this.form.elements[name].value = customer[name] || '';
    });
    this.form.elements.department.value = customer.department || '';
    this.populateCities(customer.department, customer.city);
    if (draft.paymentMethod === 'cod' || draft.paymentMethod === 'card_pse') {
      this.form.elements.paymentMethod.value = draft.paymentMethod;
    }
    if (draft.shippingRateHandle) {
      this.selectedShippingRate = { handle: draft.shippingRateHandle };
    }
    this.updateSubmitLabel();
  }

  configureShipping() {
    ['department', 'city', 'address', 'addressExtra'].forEach((name) => {
      const field = this.form.elements[name];
      field.addEventListener(field.tagName === 'SELECT' ? 'change' : 'input', () => this.scheduleShippingRates());
    });
    this.scheduleShippingRates();
  }

  scheduleShippingRates() {
    clearTimeout(this.shippingTimer);
    const customer = this.customer();
    const hasDestination = Boolean(customer.department && customer.city && customer.address);
    const defaultDepartment = Object.keys(this.locations)[0];
    const destination = hasDestination ? customer : {
      department: defaultDepartment,
      city: this.locations[defaultDepartment][0],
      address: 'Dirección por confirmar',
      addressExtra: '',
    };
    const destinationKey = [destination.department, destination.city, destination.address, destination.addressExtra].join('|');
    if (destinationKey === this.shippingDestinationKey) return;
    this.shippingDestinationKey = destinationKey;
    this.shippingRequestId = (this.shippingRequestId || 0) + 1;
    this.setShippingError('');
    if (!this.shippingRates.length) this.renderShippingStatus('Consultando opciones de envío...', 'loading');
    const requestId = this.shippingRequestId;
    this.shippingTimer = setTimeout(() => this.loadShippingRates(requestId, destination), hasDestination ? 650 : 0);
  }

  async loadShippingRates(requestId, destination) {
    try {
      const data = await this.request('/apps/vanta/commerce/shipping-rates', {
        draft: this.draft,
        destination,
      });
      if (requestId !== this.shippingRequestId) return;
      this.shippingRates = data.rates || [];
      if (!this.shippingRates.length) {
        this.renderShippingStatus('No encontramos métodos de envío para esta dirección.', 'error');
        return;
      }
      this.renderShippingRates();
    } catch (error) {
      if (requestId !== this.shippingRequestId) return;
      this.renderShippingStatus(error.message || 'No pudimos consultar los métodos de envío.', 'error');
    }
  }

  renderShippingStatus(message, state = '') {
    const status = this.root.querySelector('[data-vanta-shipping-status]');
    const options = this.root.querySelector('[data-vanta-shipping-options]');
    status.className = `vanta-checkout__shipping-status${state ? ` is-${state}` : ''}`;
    status.replaceChildren();
    if (state === 'loading') {
      const spinner = document.createElement('span');
      spinner.className = 'vanta-checkout__shipping-spinner';
      status.appendChild(spinner);
    }
    status.append(document.createTextNode(message));
    status.hidden = false;
    options.hidden = true;
    options.replaceChildren();
  }

  renderShippingRates() {
    const status = this.root.querySelector('[data-vanta-shipping-status]');
    const options = this.root.querySelector('[data-vanta-shipping-options]');
    const selectedHandle = this.selectedShippingRate?.handle;
    const selectedIndex = Math.max(0, this.shippingRates.findIndex((rate) => rate.handle === selectedHandle));
    status.hidden = true;
    options.hidden = false;
    options.replaceChildren();
    this.shippingRates.forEach((rate, index) => {
      const label = document.createElement('label');
      label.className = 'vanta-checkout__shipping-option';
      const input = document.createElement('input');
      input.type = 'radio';
      input.name = 'shippingRate';
      input.value = rate.handle;
      input.checked = index === selectedIndex;
      input.addEventListener('change', () => {
        this.selectedShippingRate = rate;
        this.setShippingError('');
        this.renderTotals();
      });
      const details = document.createElement('span');
      const title = document.createElement('strong');
      title.textContent = rate.title;
      const note = document.createElement('small');
      const normalizedTitle = rate.title.toLocaleLowerCase('es');
      if (normalizedTitle.includes('prioritario')) {
        note.textContent = '2 a 5 días hábiles';
      } else if (normalizedTitle.includes('estándar') || normalizedTitle.includes('estandar')) {
        note.textContent = '3 a 8 días hábiles';
      } else {
        note.textContent = 'Disponible para tu pedido';
      }
      details.append(title, note);
      const price = document.createElement('b');
      price.className = 'vanta-checkout__shipping-price';
      price.textContent = rate.amount > 0 ? this.formatMoney(rate.amount) : 'Gratis';
      label.append(input, details, price);
      options.appendChild(label);
    });
    this.selectedShippingRate = this.shippingRates[selectedIndex];
    this.renderTotals();
  }

  configureValidation() {
    const phone = this.form.elements.phone;
    phone.addEventListener('input', () => {
      phone.value = phone.value.replace(/\D/g, '').slice(0, 10);
    });
    this.form.querySelectorAll('input, select').forEach((field) => {
      const eventName = field.tagName === 'SELECT' || field.type === 'radio' ? 'change' : 'input';
      field.addEventListener(eventName, () => this.setFieldError(field, ''));
    });
  }

  customer() {
    const data = new FormData(this.form);
    return {
      fullName: String(data.get('fullName') || ''), phone: String(data.get('phone') || ''),
      email: String(data.get('email') || ''), department: String(data.get('department') || ''),
      city: String(data.get('city') || ''), address: String(data.get('address') || ''),
      addressExtra: String(data.get('addressExtra') || ''),
    };
  }

  validate() {
    const customer = this.customer();
    const errors = {
      fullName: customer.fullName ? '' : 'Ingresa tu nombre completo.',
      phone: !customer.phone
        ? 'Ingresa tu número de teléfono.'
        : /^\d{10}$/.test(customer.phone)
          ? ''
          : 'El teléfono debe tener exactamente 10 dígitos.',
      email: !customer.email
        ? (this.settings.emailRequired ? 'Ingresa tu correo electrónico.' : '')
        : /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(customer.email)
          ? ''
          : 'Ingresa un correo electrónico válido.',
      department: customer.department ? '' : 'Selecciona un departamento.',
      city: customer.city ? '' : 'Selecciona la ciudad de entrega.',
      address: customer.address ? '' : 'Ingresa la dirección de entrega.',
      addressExtra: customer.addressExtra ? '' : 'Ingresa el barrio, apartamento o una indicación.',
    };
    let firstInvalidField;
    Object.entries(errors).forEach(([name, message]) => {
      const field = this.form.elements[name];
      this.setFieldError(field, message);
      if (message && !firstInvalidField) firstInvalidField = field;
    });
    const shippingError = this.selectedShippingRate ? '' : 'Selecciona un método de envío.';
    this.setShippingError(shippingError);
    if (shippingError && !firstInvalidField) {
      firstInvalidField = this.root.querySelector('[data-vanta-shipping]');
    }
    if (firstInvalidField) {
      firstInvalidField.focus?.();
      throw new Error('Revisa los campos marcados antes de continuar.');
    }
  }

  setFieldError(field, message) {
    const container = field?.closest('.vanta-checkout__field');
    if (!container) return;
    let error = container.querySelector('.vanta-checkout__field-error');
    if (!error) {
      error = document.createElement('small');
      error.className = 'vanta-checkout__field-error';
      error.setAttribute('aria-live', 'polite');
      container.appendChild(error);
    }
    container.classList.toggle('is-invalid', Boolean(message));
    field.setAttribute('aria-invalid', message ? 'true' : 'false');
    error.textContent = message;
    error.hidden = !message;
  }

  setShippingError(message) {
    const container = this.root.querySelector('[data-vanta-shipping]');
    let error = container.querySelector('.vanta-checkout__field-error');
    if (!error) {
      error = document.createElement('small');
      error.className = 'vanta-checkout__field-error';
      error.setAttribute('aria-live', 'polite');
      container.appendChild(error);
    }
    container.classList.toggle('is-invalid', Boolean(message));
    error.textContent = message;
    error.hidden = !message;
  }

  async submit(event) {
    event.preventDefault();
    const buttons = this.form.querySelectorAll('[type="submit"]');
    try {
      this.validate(); this.showError('');
      buttons.forEach((button) => { button.disabled = true; button.textContent = 'Procesando...'; });
      const draft = {
        ...this.draft,
        customer: this.customer(),
        paymentMethod: this.form.elements.paymentMethod.value,
        shippingRateHandle: this.selectedShippingRate.handle,
      };
      if (draft.paymentMethod === 'cod') {
        const storageKey = `vanta_idempotency_${this.draft.source}`;
        const signature = JSON.stringify(draft);
        let storedAttempt;
        try {
          storedAttempt = JSON.parse(sessionStorage.getItem(storageKey) || 'null');
        } catch {
          storedAttempt = null;
        }
        const idempotencyKey = storedAttempt?.signature === signature
          ? storedAttempt.key
          : crypto.randomUUID();
        sessionStorage.setItem(storageKey, JSON.stringify({ key: idempotencyKey, signature }));
        const result = await this.request('/apps/vanta/commerce/cod-order', { draft, idempotencyKey });
        await this.clearCart();
        sessionStorage.removeItem(storageKey); sessionStorage.removeItem('vanta_purchase_draft');
        this.clearRecovery();
        location.assign(result.order.statusPageUrl);
      } else {
        const result = await this.request('/apps/vanta/commerce/card-checkout', { draft });
        this.saveRecovery(draft);
        await this.clearCart();
        sessionStorage.removeItem('vanta_purchase_draft');
        location.assign(result.checkoutUrl);
      }
    } catch (error) {
      this.showError(error.message || 'No pudimos procesar el pedido.');
      buttons.forEach((button) => { button.disabled = false; });
      this.updateSubmitLabel();
    }
  }

  showError(message) {
    this.error.textContent = message;
    this.error.hidden = !message;
    if (message) this.error.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }
}

document.querySelectorAll('[data-vanta-checkout]').forEach((root) => new VantaCheckout(root));
